// src/pages/account/DeveloperTools.js
import React, { useState } from 'react';
import { dynastyTheme } from '../../services/colorService';
import apiService from '../../services/apiService';
import { Trash2, Database, AlertTriangle, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

/**
 * DeveloperTools Component
 * 
 * Provides admin/developer utilities for managing the application.
 * Currently includes:
 * - Delete all leagues (for fresh start during development)
 * - View league status
 */
const DeveloperTools = ({ setMessage }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [leagueStatus, setLeagueStatus] = useState(null);
    const [cleanupResult, setCleanupResult] = useState(null);

    /**
     * Fetch current league status from the API
     * Shows how many leagues, teams, and memberships exist
     */
    const fetchLeagueStatus = async () => {
        setIsLoading(true);
        try {
            const response = await apiService.get('/api/utilities/admin/league-status');
            setLeagueStatus(response.data);
            setMessage({ 
                text: 'League status loaded successfully', 
                type: 'success' 
            });
        } catch (error) {
            console.error('Error fetching league status:', error);
            setMessage({ 
                text: `Failed to fetch league status: ${error.response?.data?.detail || error.message}`, 
                type: 'error' 
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Execute the cleanup - delete all leagues
     * This is a destructive operation that removes all league data
     */
    const executeCleanup = async () => {
        setIsLoading(true);
        setShowConfirmDialog(false);
        try {
            const response = await apiService.post('/api/utilities/admin/cleanup-all-leagues');
            setCleanupResult(response.data);
            
            if (response.data.success) {
                setMessage({ 
                    text: '✅ All leagues deleted successfully!', 
                    type: 'success' 
                });
                // Refresh status to show clean slate
                setTimeout(() => fetchLeagueStatus(), 1000);
            } else {
                setMessage({ 
                    text: '⚠️ Cleanup completed with errors - check details below', 
                    type: 'error' 
                });
            }
        } catch (error) {
            console.error('Error during cleanup:', error);
            setMessage({ 
                text: `Failed to cleanup leagues: ${error.response?.data?.detail || error.message}`, 
                type: 'error' 
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={dynastyTheme.components.card.base}>
            {/* Header */}
            <div className="mb-8">
                <h2 className={`${dynastyTheme.components.heading.h2} mb-2`}>
                    Developer Tools
                </h2>
                <p className={dynastyTheme.classes.text.neutralLight}>
                    Utilities for managing application data during development
                </p>
            </div>

            {/* Warning Banner */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-start">
                <AlertTriangle size={24} className="text-red-400 mr-3 flex-shrink-0 mt-1" />
                <div>
                    <h3 className="text-red-400 font-bold mb-1">⚠️ Destructive Operations</h3>
                    <p className="text-red-400/80 text-sm">
                        These tools perform irreversible operations on your database. 
                        Use with caution and only during development/testing.
                    </p>
                </div>
            </div>

            {/* League Status Section */}
            <div className={`${dynastyTheme.components.card.highlighted} mb-6`}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <Database size={24} className="text-yellow-400 mr-3" />
                        <h3 className={dynastyTheme.components.heading.h3}>
                            League Status
                        </h3>
                    </div>
                    <button
                        onClick={fetchLeagueStatus}
                        disabled={isLoading}
                        className={`${dynastyTheme.components.button.primary} flex items-center`}
                    >
                        <RefreshCw size={18} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                        {isLoading ? 'Loading...' : 'Check Status'}
                    </button>
                </div>

                {leagueStatus && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-gray-800 rounded-lg p-4 text-center">
                                <div className="text-3xl font-bold text-yellow-400">
                                    {leagueStatus.status.registered_leagues}
                                </div>
                                <div className="text-sm text-gray-400 mt-1">Leagues</div>
                            </div>
                            <div className="bg-gray-800 rounded-lg p-4 text-center">
                                <div className="text-3xl font-bold text-yellow-400">
                                    {leagueStatus.status.total_teams}
                                </div>
                                <div className="text-sm text-gray-400 mt-1">Teams</div>
                            </div>
                            <div className="bg-gray-800 rounded-lg p-4 text-center">
                                <div className="text-3xl font-bold text-yellow-400">
                                    {leagueStatus.status.total_memberships}
                                </div>
                                <div className="text-sm text-gray-400 mt-1">Memberships</div>
                            </div>
                        </div>

                        {leagueStatus.clean_state && (
                            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center">
                                <CheckCircle size={20} className="text-emerald-400 mr-2" />
                                <span className="text-emerald-400 text-sm">
                                    Database is clean - no leagues exist
                                </span>
                            </div>
                        )}

                        {leagueStatus.leagues && leagueStatus.leagues.length > 0 && (
                            <div className="mt-4">
                                <h4 className="text-sm font-semibold text-gray-400 mb-2">
                                    Existing Leagues:
                                </h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {leagueStatus.leagues.map((league) => (
                                        <div 
                                            key={league.league_id}
                                            className="bg-gray-800 rounded p-3 flex items-center justify-between"
                                        >
                                            <div>
                                                <div className="text-white font-semibold">
                                                    {league.league_name}
                                                </div>
                                                <div className="text-xs text-gray-400">
                                                    {league.team_count} teams • Created {new Date(league.created_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-500 font-mono">
                                                {league.league_id.substring(0, 8)}...
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Delete All Leagues Section */}
            <div className={`${dynastyTheme.components.card.highlighted} border-2 border-red-500/20`}>
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start flex-1">
                        <Trash2 size={24} className="text-red-400 mr-3 flex-shrink-0 mt-1" />
                        <div>
                            <h3 className={`${dynastyTheme.components.heading.h3} text-red-400`}>
                                Delete All Leagues
                            </h3>
                            <p className="text-sm text-gray-400 mt-1">
                                Permanently removes all leagues, teams, and related data from the database.
                                This action cannot be undone.
                            </p>
                            <ul className="mt-3 space-y-1 text-xs text-gray-500">
                                <li>• Deletes all league settings and configurations</li>
                                <li>• Removes all teams and rosters</li>
                                <li>• Clears all trade history and transactions</li>
                                <li>• Removes league memberships and invitations</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <button
                    onClick={() => setShowConfirmDialog(true)}
                    disabled={isLoading}
                    className={`${dynastyTheme.components.button.secondary} border-red-500 text-red-400 hover:bg-red-500/10 w-full mt-4 flex items-center justify-center`}
                >
                    <Trash2 size={18} className="mr-2" />
                    Delete All Leagues
                </button>
            </div>

            {/* Cleanup Result Display */}
            {cleanupResult && (
                <div className={`${dynastyTheme.components.card.base} mt-6`}>
                    <div className="flex items-center mb-4">
                        {cleanupResult.success ? (
                            <CheckCircle size={24} className="text-emerald-400 mr-3" />
                        ) : (
                            <XCircle size={24} className="text-red-400 mr-3" />
                        )}
                        <h3 className={dynastyTheme.components.heading.h3}>
                            Cleanup Result
                        </h3>
                    </div>

                    <div className="space-y-3">
                        <div className={`rounded-lg p-3 ${
                            cleanupResult.success 
                                ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                : 'bg-red-500/10 border border-red-500/30'
                        }`}>
                            <p className={cleanupResult.success ? 'text-emerald-400' : 'text-red-400'}>
                                {cleanupResult.message}
                            </p>
                        </div>

                        {cleanupResult.summary && (
                            <div className="bg-gray-800 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-gray-400 mb-2">Summary:</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-gray-500">Tables cleared:</span>
                                        <span className="text-white ml-2">
                                            {Object.keys(cleanupResult.summary.tables_cleared || {}).length}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Remaining leagues:</span>
                                        <span className="text-white ml-2">
                                            {cleanupResult.summary.remaining_leagues}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Remaining teams:</span>
                                        <span className="text-white ml-2">
                                            {cleanupResult.summary.remaining_teams}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Errors:</span>
                                        <span className={`ml-2 ${
                                            cleanupResult.summary.errors?.length > 0 ? 'text-red-400' : 'text-emerald-400'
                                        }`}>
                                            {cleanupResult.summary.errors?.length || 0}
                                        </span>
                                    </div>
                                </div>

                                {cleanupResult.summary.errors && cleanupResult.summary.errors.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-gray-700">
                                        <h5 className="text-xs font-semibold text-red-400 mb-2">Errors:</h5>
                                        <ul className="space-y-1">
                                            {cleanupResult.summary.errors.map((error, idx) => (
                                                <li key={idx} className="text-xs text-red-400/80">
                                                    • {error}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className={`${dynastyTheme.components.card.base} max-w-md w-full border-2 border-red-500/30`}>
                        <div className="flex items-center mb-4">
                            <AlertTriangle size={32} className="text-red-400 mr-3" />
                            <h3 className="text-2xl font-bold text-red-400">
                                Confirm Deletion
                            </h3>
                        </div>

                        <p className="text-white mb-4">
                            Are you absolutely sure you want to delete <strong>ALL leagues</strong>?
                        </p>

                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6">
                            <p className="text-red-400 text-sm font-semibold mb-2">
                                This will permanently delete:
                            </p>
                            <ul className="text-red-400/80 text-sm space-y-1">
                                <li>• All leagues and their settings</li>
                                <li>• All teams and rosters</li>
                                <li>• All trades and transactions</li>
                                <li>• All league memberships</li>
                                <li>• All player statistics and history</li>
                            </ul>
                            <p className="text-red-400 font-bold text-sm mt-3">
                                This action cannot be undone!
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className={dynastyTheme.components.button.secondary}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeCleanup}
                                className={`${dynastyTheme.components.button.primary} bg-red-500 hover:bg-red-600`}
                            >
                                Yes, Delete Everything
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeveloperTools;
